import { LL } from 'i18n/i18n.service';
import { ToastOptions, ToastType } from 'components/toast/toast-host';
import { TranslatedString } from 'models/translated-string';

/**
 * The wording of the "food added" confirmation (#4967).
 *
 * There are two completely different ways a food reaches a meal, and each shows this
 * confirmation from its own code:
 *
 * - the portion screen writes the food into the day's log itself (its "Log" mode), and
 * - the portion screen hands the food back to an open meal, which adds it to its own list
 *   (`ingredient-list.ts`).
 *
 * They cannot share the ACTION — one writes a stored document, the other edits a page's
 * working copy, and their Undos therefore differ — but the user must not be able to tell the
 * two apart by reading the message. Keeping the wording here is what stops the two drifting
 * as either side is edited later.
 */
export interface FoodAddedToastParts {
  /** The food as the user knows it, e.g. "Scrambled Eggs". */
  foodName: string;
  /** How much, already formatted for reading — "2 large", or "100 g". */
  amountDescription: string;
  /**
   * Where it went, in the user's own language — "Meal 1", or a recipe's name.
   *
   * May be EMPTY, and that is a normal case rather than a caller's mistake: a recipe the user
   * has not titled yet has no name to give. The message then leaves out the destination
   * entirely instead of trailing off after "added to".
   */
  mealName: string;
}

/**
 * The confirmation line — "Scrambled Eggs 2 large added to Meal 1", or "Scrambled Eggs 2 large
 * added" when there is no destination name to give.
 */
export function buildFoodAddedToastMessage(parts: FoodAddedToastParts): TranslatedString {
  if (!parts.mealName) {
    return LL.editIngredient.foodAddedToastNoDestination({
      food: parts.foodName,
      amount: parts.amountDescription,
    });
  }
  return LL.editIngredient.foodAddedToast({
    food: parts.foodName,
    amount: parts.amountDescription,
    meal: parts.mealName,
  });
}

/** How long the confirmation stays on screen. Long enough to read it and reach Undo. */
export const FOOD_ADDED_TOAST_DURATION_MS = 5000;

/**
 * How the confirmation LOOKS, shared by both routes for the same reason the wording is (#5116).
 *
 * Green because it confirms something that finished, rather than telling the user a fact.
 *
 * Bottom of the screen. The app-wide rule is top on a phone, and it stays that way for almost
 * every toast: a message nobody needs to act on gains nothing from covering the bottom of a
 * list. The exceptions are the toasts carrying an Undo a user may want within a second of
 * seeing it — this one, and the Auto-Tune result (#4971, worded and positioned in
 * `client/src/pages/edit-meal/edit-meal.ts`) — because the top of a phone is the furthest
 * point from a thumb. On a wider screen `position` changes nothing — every toast is already at
 * the bottom there.
 */
export const FOOD_ADDED_TOAST_PRESENTATION = {
  type: ToastType.SuccessStrong,
  position: 'bottom',
  duration: FOOD_ADDED_TOAST_DURATION_MS,
} as const satisfies Pick<ToastOptions, 'type' | 'position' | 'duration'>;
